import React, { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentItem from "../../../../../components/layout/content/ContentItem";

import Heading from "../../../../../components/typography/Heading";
import Text from "../../../../../components/typography/Text";

import EmptyState from "../../../../../components/feedback/EmptyState";
import Button from "../../../../../components/buttons/Button";
import { EllipsisMenu } from "../../../../../components/menu/ellipsis/EllipsisMenu";

import { usePrivateTalentPulse } from "../hooks/usePrivateTalentPulse";
import PrivateTalentPulseForm from "../forms/PrivateTalentPulseForm";
import PrivateTalentPulseResult from "../components/PrivateTalentPulseResult";

import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import { getPrivateTalentPulseDataset } from "../services/privateTalentPulseService";

import "./PrivateTalentPulseView.css";

const EMPTY_LIST = [];

const getSportId = (item) => {
  const value = item?.talentSportId ?? item?.sportId ?? item?.talent_sport_id;

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const getSportName = (item) => String(item?.sport || "").trim();

const findSportPulse = (sport, pulses) => {
  const sportId = getSportId(sport);
  const sportName = getSportName(sport).toLowerCase();

  return pulses.find((pulse) => {
    const pulseSportId = getSportId(pulse);

    if (sportId && pulseSportId) {
      return sportId === pulseSportId;
    }

    return sportName && getSportName(pulse).toLowerCase() === sportName;
  });
};

export default function PrivateTalentPulseView({
  talentId,
  talentTag,
  sports = EMPTY_LIST,
  readOnly = false,
}) {
  const {
    pulses,
    dataset,
    loading,
    loadingDataset,
    saving,
    error,
    datasetError,
    getPulses,
    getDataset,
    savePulse,
    removePulse,
  } = usePrivateTalentPulse(readOnly);

  const [selectedSport, setSelectedSport] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [sportMetrics, setSportMetrics] = useState({});

  const requestedMetrics = useRef(new Set());

  const registeredSports = Array.isArray(sports) ? sports : EMPTY_LIST;

  const displayedPulses = Array.isArray(pulses) ? pulses : EMPTY_LIST;

  useEffect(() => {
    if (readOnly && !talentTag) return;
    if (!readOnly && !talentId) return;

    void getPulses(talentId, talentTag).catch((err) => {
      console.error("Load Talent Pulse failed:", err);
    });
  }, [talentId, talentTag, readOnly]);

  useEffect(() => {
    if (readOnly) return;

    const completedSports = registeredSports.filter((sport) =>
      Boolean(findSportPulse(sport, displayedPulses)),
    );

    let cancelled = false;

    const loadMetrics = async () => {
      const missingMetrics = completedSports.filter((sport) => {
        const pulse = findSportPulse(sport, displayedPulses);
        const name = getSportName(sport);

        if (!name) return false;
        if (pulse?.metricLabels?.length === 5) return false;
        if (sportMetrics[name]?.length === 5) return false;
        if (requestedMetrics.current.has(name)) return false;

        requestedMetrics.current.add(name);
        return true;
      });

      if (!missingMetrics.length) return;

      const results = await Promise.allSettled(
        missingMetrics.map(async (sport) => {
          const name = getSportName(sport);
          const response = await getPrivateTalentPulseDataset(name);
          const result = response.data?.data || response.data;

          return {
            name,
            metrics: Array.isArray(result?.metrics) ? result.metrics : [],
          };
        }),
      );

      results.forEach((result, index) => {
        if (result.status === "rejected" || result.value.metrics.length !== 5) {
          requestedMetrics.current.delete(getSportName(missingMetrics[index]));
        }
      });

      if (cancelled) return;

      setSportMetrics((current) => {
        const next = { ...current };
        let changed = false;

        results.forEach((result) => {
          if (
            result.status === "fulfilled" &&
            result.value.metrics.length === 5
          ) {
            next[result.value.name] = result.value.metrics;
            changed = true;
          }
        });

        return changed ? next : current;
      });
    };

    void loadMetrics();

    return () => {
      cancelled = true;
    };
  }, [readOnly, registeredSports, displayedPulses, sportMetrics]);

  useEffect(() => {
    if (readOnly || !showTest || !selectedSport?.sport) {
      return;
    }

    void getDataset(selectedSport.sport).catch((err) => {
      console.error("Load Pulse dataset failed:", err);
    });
  }, [readOnly, showTest, selectedSport?.sport]);

  const openTest = (sport) => {
    if (readOnly || !talentId || !getSportId(sport) || saving) {
      return;
    }

    setSelectedSport(sport);
    setShowTest(true);
  };

  const closeTest = () => {
    if (saving) return;

    setShowTest(false);
    setSelectedSport(null);
  };

  const handleSave = async (answers) => {
    if (readOnly || !selectedSport || saving) return;

    try {
      await savePulse(talentId, getSportId(selectedSport), answers);

      setShowTest(false);
      setSelectedSport(null);
    } catch (err) {
      console.error("Save Talent Pulse failed:", err);
    }
  };

  const handleDeletePulse = async (pulse) => {
    if (readOnly || !pulse?.pulseId || saving) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete your ${pulse.sport} Talent Pulse? This action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await removePulse(pulse.pulseId);
    } catch (err) {
      console.error("Delete Talent Pulse failed:", err);
    }
  };

  const visibleSports = readOnly
    ? displayedPulses.map((pulse) => ({
        sportId: pulse.talentSportId,
        sport: pulse.sport,
        pulse,
      }))
    : registeredSports;

  if (!readOnly && !talentId) return null;

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="Talent Pulse"
          subTitle={
            readOnly
              ? "Sporting qualities and assessment results."
              : "Discover your sporting qualities for each sport."
          }
        />

        {loading ? (
          <Text as="p" tone="secondary" size="sm">
            Loading Talent Pulse...
          </Text>
        ) : error ? (
          <div className="private-talent-pulse-view__error" role="alert">
            <Text as="p" tone="secondary" size="sm">
              {error}
            </Text>

            <Button
              value="Retry"
              variant="primary-outlined"
              onClick={() => {
                void getPulses(talentId, talentTag).catch(() => {});
              }}
            />
          </div>
        ) : readOnly && !displayedPulses.length ? (
          <EmptyState
            icon={<Activity size={28} />}
            title="No Talent Pulse yet"
            description="Sport Pulse assessments will appear here when available."
          />
        ) : !readOnly && !registeredSports.length ? (
          <EmptyState
            icon={<Activity size={28} />}
            title="Add a sport first"
            description="Add a sport to your Talent ID before taking a Pulse assessment."
          />
        ) : (
          <div className="private-talent-pulse-view__sports">
            {visibleSports.map((sport) => {
              const pulse = readOnly
                ? sport.pulse
                : findSportPulse(sport, displayedPulses);

              const sportId = getSportId(sport);
              const sportName = getSportName(sport);

              const metrics =
                pulse?.metricLabels?.length === 5
                  ? pulse.metricLabels
                  : !readOnly
                    ? sportMetrics[sportName] || []
                    : [];

              return (
                <ContentItem
                  key={pulse?.pulseId || sportId || sportName}
                  className="private-talent-pulse-view__sport"
                >
                  <div className="private-talent-pulse-view__sport-header">
                    <div className="private-talent-pulse-view__sport-info">
                      <Heading as="h3" size="sm">
                        {sportName}
                      </Heading>

                      <Text as="p" tone="secondary" size="sm">
                        {pulse ? "Assessment completed" : "No assessment saved"}
                      </Text>
                    </div>

                    {!readOnly && (
                      <EllipsisMenu
                        ariaLabel={`${sportName} Pulse options`}
                        position="right"
                        disabled={saving || !sportId}
                        options={[
                          {
                            id: pulse ? "retake" : "take",
                            label: pulse ? "Retake Test" : "Take Pulse Test",
                            action: () => openTest(sport),
                          },
                          ...(pulse
                            ? [
                                {
                                  id: "delete",
                                  label: "Delete Pulse",
                                  className: "is-danger",
                                  action: () => handleDeletePulse(pulse),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </div>

                  {pulse && (
                    <PrivateTalentPulseResult
                      pulse={pulse}
                      metrics={metrics}
                      readOnly={readOnly}
                    />
                  )}
                </ContentItem>
              );
            })}
          </div>
        )}
      </ContentPanel>

      {!readOnly && showTest && selectedSport && (
        <PrivateTalentPulseForm
          key={getSportId(selectedSport)}
          sport={selectedSport.sport}
          dataset={dataset}
          loading={loadingDataset}
          saving={saving}
          error={datasetError || error}
          onSave={handleSave}
          onClose={closeTest}
        />
      )}
    </ViewContainer>
  );
}
